import { getBidderId, getUserData } from '../redux/modules/user';
import { getDeviceType, isUiMobile } from '../redux/modules/browser';
import { getBuildNumber } from '../redux/modules/config';
import { isExperimentActive } from '../redux/modules/googleOptimize';
import uuidv4 from 'uuid/v4';

const defaultConfig = {
    enableResourceTiming: true,
    resourceTimingDomains: null,
    url: 'http://localhost:8080',
    waitAfterNavigation: 1500,
    waitAfterOnLoad: 3000,
};

export const initializeRumSession = ({ getState }, cfg) => {
    cfg = { ...defaultConfig, ...cfg };
    if (!win.performance || !win.performance.mark) {
        return -1;
    }

    const session = new RUM(getState, cfg);
    const { history } = win;
    let currentBeaconHandle = null;

    win.requestIdleCallback(() => {
        win.performance.mark('cpu-idle');
        currentBeaconHandle = session.sendBeacon();
    });

    history.pushState = (ps => (state, title, url) => {
        session.pushState(state, title, url);

        if (currentBeaconHandle) {
            clearTimeout(currentBeaconHandle);
        }

        win.performance.mark('user-navigation-start');
        ps(state, title, url);
        win.requestIdleCallback(() => {
            win.performance.mark('cpu-idle');
            currentBeaconHandle = session.sendBeacon();
        });
    })(history.pushState.bind(history));
};

class RUM {
    constructor(getState, cfg) {
        this.sessionId = uuidv4();
        this.startTime = Date.now();
        this.getState = getState;
        this.renderType = 'server';
        this.cfg = cfg;
        this.referrer = win.document.referrer;
    }

    getMetrics = () => {
        let navigationTime = null;
        let firstRenderMetrics = {};

        if (this.renderType === 'client') {
            win.performance.measure('navigation-time', 'user-navigation-start', 'root-finished-update');
            [navigationTime] = win.performance
                .getEntriesByName('navigation-time', 'measure')
                .map(({ duration }) => duration);
        } else {
            const [firstPaint, firstContentfulPaint] = win.performance
                .getEntriesByType('paint')
                .map(({ startTime }) => startTime);

            const [navigationTimings] = win.performance.getEntriesByType('navigation').map(navigationTimingMapFnc);

            firstRenderMetrics = {
                firstContentfulPaint,
                firstPaint,
                ...navigationTimings,
            };
        }

        const measurePoint = this.renderType === 'client' ? 'user-navigation-start' : undefined;
        win.performance.measure('time-to-interactive', measurePoint, 'cpu-idle');
        const [timeToInteractive] = win.performance
            .getEntriesByName('time-to-interactive', 'measure')
            .map(({ duration }) => duration);

        const { entries, totals } = getResourceTimings(this.cfg);

        return {
            domNodeCount: document.getElementsByTagName('*').length,
            ...firstRenderMetrics,
            ...(win.performance.memory || {}),
            navigationTime,
            navigationType: this.navigationType,
            renderType: this.renderType,
            resourceTimings: { entries, totals },
            scriptCount: document.getElementsByTagName('script').length,
            thirdPartyScriptCount: Array.apply([], document.getElementsByTagName('script')).filter(
                s => !(s.src || '').includes(win.location.hostname),
            ).length,
            timeToInteractive,
            visibleImages: document.getElementsByTagName('img').length,
        };
    };

    pushState = () => {
        const {
            location: { href },
        } = win;
        this.referrer = href;
        this.renderType = 'client';
    };

    getBeacon = () => {
        const state = this.getState();
        const {
            location: { href },
            navigator: { userAgent },
            screen,
        } = win;

        const { height, width, orientation } = screen;
        return {
            client: {
                appVersion: getBuildNumber(state) || 'LOCAL',
                deviceType: getDeviceType(state),
                isExperimentActive: isExperimentActive(state),
                isMobile: isUiMobile(state) === 'tablet' || isUiMobile(state) === 'phone',
                orientation: (orientation || {}).type,
                referrer: this.referrer,
                screenHeight: height,
                screenWidth: width,
                url: href,
                userAgent,
            },
            createdTime: Date.now(),
            metrics: this.getMetrics(),
            session: {
                duration: Date.now() - this.startTime,
                id: this.sessionId,
                startTime: this.startTime,
            },
            user: {
                bidderId: getBidderId(state),
                isHighNms: (getUserData(state) || {}).ANMS >= 4500,
            },
        };
    };

    sendBeacon = () =>
        setTimeout(async () => {
            const { url } = this.cfg;

            try {
                const beacon = this.getBeacon();
                const endpoint = `${url}/beacon`;
                const payload = JSON.stringify(beacon);

                if (!(navigator.sendBeacon && navigator.sendBeacon(endpoint, payload))) {
                    await fetch(endpoint, {
                        body: payload,
                        headers: { 'Content-Type': 'application/json' },
                        method: 'POST',
                    });
                }
            } catch (error) {
                console.error(`could not send RUM beacon to ${url}: ${error}`);
            } finally {
                win.performance.clearResourceTimings();
                win.performance.clearMarks();
                win.performance.clearMeasures();
            }
        }, this.cfg.waitAfterOnLoad);
}

const entriesFilter = resourceTimingDomains => ({ name }) => {
    if (!resourceTimingDomains) {
        return true;
    }
    const rtimingsLen = resourceTimingDomains.length;
    if (rtimingsLen === 0) {
        return false;
    }
    for (let i = 0; i < rtimingsLen; i++) {
        if (name.match(resourceTimingDomains[i])) {
            return true;
        }
    }

    return false;
};

const win = window || global || {};

win.requestIdleCallback =
    win.requestIdleCallback ||
    function(cb) {
        var start = win.performance.now();
        return setTimeout(() => {
            cb({
                didTimeout: false,
                timeRemaining: () => Math.max(0, 50 - (win.performance.now() - start)),
            });
        }, 1);
    };

win.cancelIdleCallback =
    win.cancelIdleCallback ||
    function(id) {
        clearTimeout(id);
    };

const getResourceTimings = ({ enableResourceTiming, resourceTimingDomains }) => {
    const entries = win.performance.getEntriesByType('resource');
    return {
        entries: enableResourceTiming
            ? entries
                  .filter(entriesFilter(resourceTimingDomains))
                  .map(
                      ({
                          decodedBodySize,
                          duration,
                          encodedBodySize,
                          fetchStart,
                          initiatorType,
                          name,
                          nextHopProtocol,
                          responseEnd,
                          transferSize,
                      }) => ({
                          decodedBodySize: decodedBodySize > 0 ? decodedBodySize : null,
                          duration: duration > 0 ? duration : null,
                          encodedBodySize: encodedBodySize > 0 ? encodedBodySize : null,
                          end: responseEnd,
                          name: name,
                          protocol: nextHopProtocol,
                          start: fetchStart,
                          transferSize,
                          type: initiatorType,
                      }),
                  )
            : null,
        totals: entries.reduce((s, { initiatorType, encodedBodySize, decodedBodySize, duration, transferSize }) => {
            const { compressedSize, size, totalDownloadTime, txSize } = s[initiatorType] || {
                compressedSize: 0,
                size: 0,
                totalDownloadTime: 0,
                txSize: 0,
            };

            s[initiatorType] = {
                compressedSize: compressedSize + encodedBodySize,
                size: size + decodedBodySize,
                totalDownloadTime: totalDownloadTime + duration,
                txSize: transferSize + txSize,
            };

            return s;
        }, {}),
    };
};

/* eslint-disable sort-keys */
const navigationTimingMapFnc = ({
    duration,
    startTime,
    initiatorType,
    nextHopProtocol,
    workerStart,
    fetchStart,
    domainLookupStart,
    domainLookupEnd,
    connectStart,
    connectEnd,
    secureConnectionStart,
    requestStart,
    responseStart,
    responseEnd,
    transferSize,
    encodedBodySize,
    decodedBodySize,
    serverTiming,
    unloadEventStart,
    unloadEventEnd,
    domInteractive,
    domContentLoadedEventStart,
    domContentLoadedEventEnd,
    domComplete,
    loadEventStart,
    loadEventEnd,
}) => ({
    connectStart,
    connectEnd,
    secureConnectionStart,
    requestStart,
    responseStart,
    responseEnd,
    startTime,
    initiatorType,
    nextHopProtocol,
    workerStart,
    fetchStart,
    duration,
    domainLookupStart,
    domainLookupEnd,
    transferSize,
    encodedBodySize,
    decodedBodySize,
    serverTiming,
    unloadEventStart,
    unloadEventEnd,
    domInteractive,
    domContentLoadedEventStart,
    domContentLoadedEventEnd,
    domComplete,
    loadEventStart,
    loadEventEnd,
});
